import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getWorldCupSchedule } from 'service/base';

import './knockoutChart.css';

const KO_ORDER = [
  { section: 'Vòng 32 đội', label: 'Vòng 32 đội' },
  { section: 'Vòng 16 đội', label: 'Vòng 16 đội' },
  { section: 'Vòng Tứ Kết', label: 'Tứ kết' },
  { section: 'Vòng Bán Kết', label: 'Bán kết' },
  { section: 'Chung Kết', label: 'Chung kết' }
];
const THIRD_PLACE_SECTION = 'Tranh Hạng Ba';

/**
 * Override tạm thời cho Trận 93/94: dữ liệu BE đang gán nhầm số "Trận X" chéo nhau giữa
 * 4 trận Vòng 32 (81-84) nên placeholder "Thắng 83/84" và "Thắng 81/82" của BE trỏ sai cặp
 * so với sơ đồ thật. Ép đúng cặp hiển thị (theo nhãn "Trận X" đang có trên màn hình) cho tới
 * khi BE sửa lại nhãn gốc — xoá override này sau khi BE cập nhật xong.
 * Key: số trận nextSection (Vòng 16) → 2 số trận targetSection (Vòng 32) ghép cặp.
 */
const MANUAL_FEEDER_OVERRIDES = {
  93: [82, 83],
  94: [81, 84]
};

const RE_PLACEHOLDER = /^(?:Thắng|Thua)/i;
const RE_DIGITS = /(\d+)/;
const RE_RESULT = /^\s*(\d+)\s*[-:]\s*(\d+)/;

function feederNumber(teamName) {
  const str = String(teamName || '');
  if (!RE_PLACEHOLDER.test(str)) return null;
  const m = RE_DIGITS.exec(str);
  return m ? Number(m[1]) : null;
}

function matchNumber(match) {
  const m = RE_DIGITS.exec(String(match || ''));
  return m ? Number(m[1]) : null;
}

function parseResult(result) {
  const m = RE_RESULT.exec(String(result || ''));
  return m ? [Number(m[1]), Number(m[2])] : null;
}

/**
 * Sắp xếp các trận của `targetSection` dựa vào feeder references từ `nextSection`.
 *
 * Thứ tự ưu tiên xác định trận nguồn của mỗi slot home/away trong nextSection:
 * 1. Field `feeders: [homeNum, awayNum]` nếu BE có cung cấp (bất biến, không đổi dù tên đội update).
 * 2. Placeholder dạng "Thắng X" / "Thua X" còn nguyên (chưa có kết quả).
 * 3. Tên đội thật đã thay vào chỗ placeholder → tra theo tên đội trong targetSection (slot đã
 *    được cập nhật tên đội thắng nhưng vẫn còn là cùng 1 đội nên match được theo tên).
 */
function orderByNextRound(allMatches, targetSection, nextSection) {
  const byNum = new Map();
  const byTeamInTarget = new Map();
  for (const m of allMatches) {
    const num = matchNumber(m.match);
    if (num != null) byNum.set(num, m);
    if (m.section === targetSection) {
      if (m.home) byTeamInTarget.set(m.home, m);
      if (m.away) byTeamInTarget.set(m.away, m);
    }
  }

  const nextRound = allMatches
    .filter((m) => m.section === nextSection)
    .sort((a, b) => (matchNumber(a.match) || 0) - (matchNumber(b.match) || 0));

  const ordered = [];
  const placed = new Set();

  const pushFeeder = (m) => {
    if (!m || m.section !== targetSection) return;
    const num = matchNumber(m.match);
    if (num != null && !placed.has(num)) {
      ordered.push(m);
      placed.add(num);
    }
  };

  for (const nm of nextRound) {
    const nmNum = matchNumber(nm.match);
    const manualOverride = MANUAL_FEEDER_OVERRIDES[nmNum];
    if (manualOverride) {
      for (const num of manualOverride) pushFeeder(byNum.get(num));
      continue;
    }

    // Ưu tiên field feeders (cấu trúc cố định, không bị ảnh hưởng khi đổi tên đội).
    const feederNums = Array.isArray(nm.feeders) ? nm.feeders : [];

    if (feederNums.length > 0) {
      for (const num of feederNums) pushFeeder(byNum.get(num));
      continue;
    }

    for (const teamName of [nm.home, nm.away]) {
      const num = feederNumber(teamName);
      if (num != null) {
        // Placeholder còn nguyên ("Thắng X").
        pushFeeder(byNum.get(num));
      } else if (teamName) {
        // Tên đội thật đã được điền vào → tìm trận targetSection có đội này.
        pushFeeder(byTeamInTarget.get(teamName));
      }
    }
  }

  // Trận target chưa được xếp (dữ liệu bất thường) → thêm ở cuối.
  for (const m of allMatches
    .filter((m) => m.section === targetSection)
    .sort((a, b) => (matchNumber(a.match) || 0) - (matchNumber(b.match) || 0))) {
    const num = matchNumber(m.match);
    if (!placed.has(num)) ordered.push(m);
  }

  return ordered;
}

const Flag = memo(function Flag({ teamName, flagCodes }) {
  const code = flagCodes?.[teamName];
  if (!code) {
    return (
      <span className="kc-ball" aria-hidden="true">
        ⚽
      </span>
    );
  }
  return (
    <img
      className="kc-flag"
      src={`https://flagcdn.com/w40/${code}.png`}
      alt={teamName}
      loading="lazy"
      draggable={false}
    />
  );
});

const TeamRow = memo(function TeamRow({ name, flagCodes, isWinner }) {
  const pending = feederNumber(name) != null;
  return (
    <div className={`kc-team${isWinner ? ' kc-team--win' : ''}`}>
      {pending ? (
        <span className="kc-pending" aria-hidden="true">
          ?
        </span>
      ) : (
        <Flag teamName={name} flagCodes={flagCodes} />
      )}
      <span className={`kc-name${pending ? ' kc-name--pending' : ''}`}>{name}</span>
    </div>
  );
});

const MatchCard = memo(function MatchCard({ match, flagCodes }) {
  const score = parseResult(match.result);
  const homeWin = score ? score[0] > score[1] : false;
  const awayWin = score ? score[1] > score[0] : false;

  return (
    <div className="kc-match">
      <div className="kc-vline" aria-hidden="true" />
      <article
        className="kc-card"
        title={
          match.stadium ? `${match.stadium}${match.city ? ` · ${match.city}` : ''}` : undefined
        }
      >
        <div className="kc-card-top">
          <span className="kc-code">{match.match}</span>
          <span className="kc-dt">
            {match.date}
            {match.time ? ` · ${match.time}` : ''}
          </span>
        </div>
        <div className={`kc-card-grid${score ? ' kc-card-grid--scored' : ''}`}>
          <TeamRow name={match.home} flagCodes={flagCodes} isWinner={homeWin} />
          {score ? (
            <span className={`kc-score${homeWin ? ' kc-score--win' : ''}`}>{score[0]}</span>
          ) : null}
          <TeamRow name={match.away} flagCodes={flagCodes} isWinner={awayWin} />
          {score ? (
            <span className={`kc-score${awayWin ? ' kc-score--win' : ''}`}>{score[1]}</span>
          ) : null}
        </div>
      </article>
    </div>
  );
});

const KnockoutChart = ({ embedded = false }) => {
  const [matches, setMatches] = useState([]);
  const [flagCodes, setFlagCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    setLoadErr('');
    try {
      const res = await getWorldCupSchedule();
      if (res?.code === 0 && res?.data?.matches?.length) {
        setMatches(res.data.matches);
        setFlagCodes(res.data.flagCodes || {});
      } else {
        setMatches([]);
        setFlagCodes({});
        setLoadErr(res?.message || 'Không tải được sơ đồ thi đấu.');
      }
    } catch (e) {
      setLoadErr(e?.message || 'Lỗi mạng khi tải sơ đồ.');
      setMatches([]);
      setFlagCodes({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  useEffect(() => {
    if (embedded) return undefined;
    const prev = document.title;
    document.title = 'Sơ đồ thi đấu vòng loại trực tiếp World Cup 2026 - CM88';
    return () => {
      document.title = prev;
    };
  }, [embedded]);

  const rounds = useMemo(() => {
    if (!matches.length) return [];
    return KO_ORDER.map(({ section, label }, i) => {
      const nextSection = KO_ORDER[i + 1]?.section;
      const items =
        i === 0 && nextSection
          ? orderByNextRound(matches, section, nextSection)
          : matches
              .filter((m) => m.section === section)
              .sort((a, b) => (matchNumber(a.match) || 0) - (matchNumber(b.match) || 0));
      return { label, items };
    }).filter((r) => r.items.length);
  }, [matches]);

  const thirdPlace = useMemo(
    () => matches.find((m) => m.section === THIRD_PLACE_SECTION) || null,
    [matches]
  );

  const hasBracket = rounds.length > 0;

  const content = (
    <div className="wc-schedule-page rounded-3xl border-solid border-[#36A638] border-[3px] my-4">
      <div className="kc-page rounded-3xl">
        <div className="kc-inner">
          <header className="kc-hero">
            <h1>Sơ đồ thi đấu vòng loại trực tiếp</h1>
            <div className="kc-sub">Từ vòng 32 đội đến Trận chung kết — World Cup 2026</div>
          </header>

          {loadErr ? (
            <div className="kc-toolbar">
              <div className="kc-empty">{loadErr}</div>
              <button type="button" className="kc-retry" onClick={() => loadSchedule()}>
                Thử lại
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="kc-empty">Đang tải sơ đồ thi đấu…</div>
          ) : loadErr ? null : !hasBracket ? (
            <div className="kc-empty">Chưa có dữ liệu vòng loại trực tiếp.</div>
          ) : (
            <>
              <div className="kc-scroll">
                <div className="kc-bracket">
                  {rounds.map((round, i) => (
                    <div key={round.label} className="kc-round" style={{ '--slot-factor': 2 ** i }}>
                      <div className="kc-round-title">{round.label}</div>
                      <div className="kc-matches">
                        {round.items.map((m) => (
                          <MatchCard key={m.id} match={m} flagCodes={flagCodes} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {thirdPlace ? (
                <div className="kc-third">
                  <div className="kc-third-title">Tranh hạng Ba</div>
                  <div className="kc-third-card">
                    <MatchCard match={thirdPlace} flagCodes={flagCodes} />
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return content;
};

export default KnockoutChart;
